/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-this-in-sfc */
/* eslint-disable react/forbid-prop-types */ // this one interferes with Yup.object()
/* eslint-disable newline-per-chained-call */ // this one is misbehaving with Yup chain calls
import React, { useMemo, useRef, FunctionComponent, useEffect } from "react";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { Content } from "@iventis/translations/content/typed-content";
import * as Yup from "yup";
import { Formik, FormikProps } from "formik";
import { BehaviorSubject } from "rxjs";
import { styled } from "@iventis/styles";
import { Body2 } from "@iventis/styles/src/components/texts";
import { getDataFieldValueFromMetaData, useObservableValue, ZERO_TIME_SPAN, dataFieldIdToValueMapping, dataFieldToEmptyValueMapping } from "@iventis/utilities";
import { Schedule } from "@iventis/domain-model/model/schedule";
import { Node } from "@iventis/domain-model/model/node";
import { differenceInMinutes } from "date-fns";
import { DataField } from "@iventis/domain-model/model/dataField";
import { EventBlockSystemDataFieldType } from "@iventis/domain-model/model/eventBlockSystemDataFieldType";
import { useMachine } from "@xstate/react";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { StartTimeType } from "@iventis/domain-model/model/startTimeType";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FormWizardTemplate, Stage, ConfirmationDialogComponent, FormWizardTitle } from "@iventis/components";
import CreateSessionFormComponent from "./session-form";
import { SessionFormValues } from "./session.types";
import { defaultCreateSessionValidations } from "./session-validations";
import { createDayDateMap, createValidationForDataFields, dayNumberFromDate, getStartAndEndAtStringDigits } from "./schedule-helper-functions";
import { SportEventDataFieldNames } from "./datafields";

import { crudMachine } from "./crud-machine";
import { AddEventBlockEvent, DeleteEventBlockEvent, UpdateEventBlockEvent } from "./schedule.machine.types";
import { deleteEventBlock, createSession, editSession } from "./schedule-api-functions";
import { findHierarchyEntityById } from "./hierarchy.helpers";
import { getEventBlockTypeByCalendarLevel } from "./event-block-helpers";
import { ScheduleVersionNode } from "./schedule.types";

export interface CreateSessionProps {
    // DETAILS
    schedule: Schedule;
    selectedScheduleNode: Node;
    selectedSubScheduleId: string;
    isScheduleManager: boolean;
    scheduleVersions: ScheduleVersionNode[];
    sessionsInSelectedSchedule: Hierarchy<EventBlock>[];
    selectedSubSchedule: ScheduleVersion;
    venues: Hierarchy<VenueLocation>[];
    day: number;
    selectedVenues: Hierarchy<VenueLocation>[];
    // Helpers
    close: () => void;
    existingSession?: Hierarchy<EventBlock>;
    sendToScheduleMachine: (event: AddEventBlockEvent | UpdateEventBlockEvent | DeleteEventBlockEvent) => void;
    timezone: string;
}

const getDefaultFormValues = (scheduleDaysMap: SessionFormValues["scheduleDaysMap"]): SessionFormValues => ({
    scheduleVersion: null,
    sessionCode: {
        sessionCodePrefix: "",
        sessionCodeDigit: "",
    },
    description: "",
    sessionDay: null,
    spareDay: false,
    startTime: ZERO_TIME_SPAN,
    endTime: ZERO_TIME_SPAN,
    estimatedEndTime: false,
    duration: 0,
    venueLocation: null,
    estimatedTime: false,
    selectedEvents: [],
    zeroTimeDisabled: true,
    zeroTime: ZERO_TIME_SPAN,
    startTimeType: StartTimeType.FollowedBy,
    scheduleDaysMap,
    dataFieldValues: {},
});

const CreateSessionComponent: FunctionComponent<CreateSessionProps> = ({
    schedule,
    selectedScheduleNode,
    selectedSubScheduleId, // this prop refers to the selected Discipline(ex Sport) Schedule in this context
    scheduleVersions,
    sessionsInSelectedSchedule,
    venues,
    day,
    selectedVenues,
    isScheduleManager,
    close,
    existingSession,
    sendToScheduleMachine,
    selectedSubSchedule,
    timezone,
}) => {
    const editing = Boolean(existingSession);
    const translate = useIventisTranslate();

    const deleteConfirmationText = useMemo(
        () => ({
            title: translate(Content.schedule.create_session_form.delete.title),
            message: translate(Content.schedule.create_session_form.delete.message),
            confirm: translate(Content.schedule.create_session_form.delete.confirm),
            cancel: translate(Content.schedule.create_session_form.delete.cancel),
        }),
        [translate]
    );

    // Use level function to get the event block type from the config
    const sessionConfig: ScheduleConfigEventBlockType = useMemo(() => getEventBlockTypeByCalendarLevel(schedule.config.eventBlockTypes, 1), [schedule?.id]);
    // Use system datafield to get the code from the config
    const sessionCodeDataField = useMemo(() => {
        const codeDataField = sessionConfig.dataFields.find((dataField) => dataField.systemDataFieldType === EventBlockSystemDataFieldType.Code);
        return schedule.config.dataFields.find(({ id }) => id === codeDataField.dataFieldId);
    }, []);

    const createSessionMachine = useMemo(
        () =>
            crudMachine({
                submit: async (_, event: { payload: SessionFormValues }) => {
                    if (editing) {
                        const response = await editSession(
                            {
                                ...event.payload,
                                existingEventBlock: existingSession.item,
                                selectedEvents: event.payload.selectedEvents,
                                type: sessionConfig,
                                sessionCodeDataField,
                                timezone,
                            },
                            schedule.config.dataFields
                        );
                        return response;
                    }
                    const session = await createSession(
                        {
                            ...event.payload,
                            type: sessionConfig,
                            sessionCodeDataField,
                            timezone,
                        },
                        schedule.config.dataFields
                    );
                    return session;
                },
                onSubmit: (_, event) => sendToScheduleMachine({ type: editing ? "UPDATE_EVENT_BLOCK" : "ADD_EVENT_BLOCK", payload: event.data }),
                deleteEntity: async (_, event) => {
                    await deleteEventBlock(event.payload, { calendarLevel: 1 });
                    return event.payload;
                },
                onDelete: (_, event) => {
                    sendToScheduleMachine({ type: "DELETE_EVENT_BLOCK", payload: event.data });
                    close();
                },
            }),
        []
    );

    const [state, send] = useMachine(createSessionMachine);

    // Close when we reach a final state
    useEffect(() => {
        if (state.done) close();
    }, [state.done]);

    const sessionDataFields = useMemo(() => {
        const { dataFields } = schedule.config;
        const { dataFields: sessionDataFieldIds } = sessionConfig;
        return dataFields.filter(({ id }) => sessionDataFieldIds.find(({ dataFieldId }) => dataFieldId === id) && id !== sessionCodeDataField.id);
    }, [schedule?.id]);

    // This selection is immutable while we have the modal open - hence no dependencies
    const selectedSubschedule = useMemo(() => scheduleVersions.find((v) => v.id === selectedSubScheduleId), []);

    const topLevelDataField = useMemo(() => schedule?.config?.dataFields.find((df) => df.name === SportEventDataFieldNames.DISCIPLINE /* || "Some music related data field" */), [
        schedule?.config,
    ]);

    const scheduleDaysMap = useMemo(() => createDayDateMap(schedule.startAt, schedule.endAt, schedule.dayZero), [schedule.startAt, schedule.endAt, schedule.dayZero]);

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                ...defaultCreateSessionValidations(translate),
                dataFieldValues: Yup.object({
                    ...createValidationForDataFields(
                        sessionDataFields,
                        sessionConfig.dataFields.filter(({ dataFieldId }) => dataFieldId !== sessionCodeDataField.id)
                    ),
                }),
            }),
        [sessionDataFields]
    );

    const initialValues: SessionFormValues = useMemo(() => {
        if (editing) {
            return getFormValuesFromEventBlock(existingSession, selectedSubschedule || scheduleVersions[0], scheduleDaysMap, venues, sessionCodeDataField, timezone);
        }
        return {
            ...getDefaultFormValues(scheduleDaysMap),
            scheduleVersion: selectedSubschedule || scheduleVersions[0],
            venueLocation: selectedVenues?.length === 1 ? selectedVenues[0] : null,
            sessionDay: day,
            dataFieldValues: dataFieldToEmptyValueMapping(sessionDataFields),
        };
    }, []);

    const scheduleVersionName$ = useMemo(() => new BehaviorSubject<string>(initialValues.scheduleVersion.name), []);
    const scheduleVersionName = useObservableValue(scheduleVersionName$);

    const formRef = useRef<FormikProps<SessionFormValues>>();

    const numberOfCurrentSessionsInVersion = useMemo(() => sessionsInSelectedSchedule.filter((s) => (s.item as EventBlock).scheduleVersionId === selectedSubScheduleId).length, [
        selectedSubScheduleId,
    ]);

    useEffect(() => {
        // Get the schedule version so we can figure out the session code prefix and digits
        const code = getDataFieldValueFromMetaData(selectedSubSchedule, SportEventDataFieldNames.DISCIPLINE, "code");
        let digit: string;
        if (editing) {
            // If we are editing an existing session, we need to pull the numeric part of the session code from it, since all sessions can have a different number
            const sessionCode = existingSession.item.dataFieldValues.find((dataField) => dataField.dataField.id === sessionCodeDataField.id);
            digit = sessionCode.value.replace(code, "");
        } else {
            // If this is a new session, default the numeric part of the session code to the number of existing sessions + 1
            digit = (numberOfCurrentSessionsInVersion + 1).toLocaleString("en-US", {
                minimumIntegerDigits: 2,
                useGrouping: false,
            });
        }
        formRef.current.setFieldValue("sessionCode.sessionCodePrefix", code);
        formRef.current.setFieldValue("sessionCode.sessionCodeDigit", digit);
    }, [selectedSubSchedule]);

    const handleSubmit = () => {
        if (formRef.current && state.matches("idle.validation.valid")) {
            formRef.current.submitForm();
        }
    };

    // Since this is a single step dialog, we just use this as the form wizard template props
    const templateProps: Stage = useMemo(
        () => ({
            primaryButtonText: translate(Content.common.buttons.save),
            primaryButtonCallback: handleSubmit,
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: close }],
            tertiaryButtonText: editing ? translate(Content.schedule.create_session_form.buttons.delete_session) : undefined,
            tertiaryButtonCallback: editing ? () => send("DELETE", { payload: existingSession?.item?.id }) : undefined,
            isValid: state.matches("idle.validation.valid"),
        }),
        [state.value]
    );

    return (
        <FormWizardTemplate
            title={
                <CreateEventHeader
                    title={editing ? translate(Content.schedule.create_session_form.headings.formTitleEdit) : translate(Content.schedule.create_session_form.headings.formTitleNew)}
                    grandParentName={selectedScheduleNode.name}
                    parentName={scheduleVersionName}
                />
            }
            stages={[templateProps]}
            currentStage={0}
            isSubmitting={state.matches("submitting")}
            isDeleting={state.matches("idle.deleteConfirmation.deleting")}
        >
            <Formik
                innerRef={formRef}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={async (values) => {
                    send("SUBMIT", { payload: values });
                }}
                validateOnMount
            >
                {(formikProps) => (
                    <CreateSessionFormComponent
                        scheduleConfig={schedule.config}
                        sessionConfig={sessionConfig}
                        dataField={topLevelDataField}
                        dataFields={sessionDataFields}
                        scheduleVersions={scheduleVersions}
                        sessionsInSelectedSchedule={sessionsInSelectedSchedule}
                        venues={venues}
                        isScheduleManager={isScheduleManager}
                        scheduleDaysMap={scheduleDaysMap}
                        setCanSubmit={(isValid) => {
                            send(isValid ? "VALID" : "INVALID");
                        }}
                        scheduleVersionName$={scheduleVersionName$}
                        editing={editing}
                        existingSession={existingSession?.item}
                        sessionCodeDataField={sessionCodeDataField}
                        timezone={timezone}
                        {...formikProps}
                    />
                )}
            </Formik>
            <ConfirmationDialogComponent
                show={state.matches("idle.deleteConfirmation.open")}
                title={deleteConfirmationText.title}
                message={deleteConfirmationText.message}
                confirmText={deleteConfirmationText.confirm}
                cancelText={deleteConfirmationText.cancel}
                handleCancel={() => {
                    send("CANCEL_DELETE");
                }}
                handleConfirm={() => {
                    send("CONFIRM_DELETE", { payload: existingSession?.item?.id });
                }}
            />
        </FormWizardTemplate>
    );
};

export const CreateEventHeader: FunctionComponent<{ grandParentName: string; parentName?: string; title: string }> = ({ grandParentName, parentName, title }) => {
    const breadcrumb = `${grandParentName} ${parentName ? `/ ${parentName}` : ""}`;
    return <FormWizardTitle header={breadcrumb} title={title} />;
};

const getFormValuesFromEventBlock = (
    eventBlock: Hierarchy<EventBlock>,
    scheduleVersion: ScheduleVersionNode,
    scheduleDaysMap: Map<number, Date>,
    venues: Hierarchy<VenueLocation>[],
    sessionCodeDataField: DataField,
    timezone: string
): SessionFormValues => ({
    scheduleVersion,
    sessionCode: {
        sessionCodePrefix: "",
        sessionCodeDigit: "",
    },
    scheduleDaysMap,
    description: eventBlock.item.description || "", // to get rid of warning in the console if it returns a null value
    sessionDay: dayNumberFromDate(scheduleDaysMap, new Date((eventBlock.item as EventBlock).startAt)),
    spareDay: false, // default to false for now, no equivalent in the back end / attached logic in the front end
    ...getStartAndEndAtStringDigits(eventBlock.item as EventBlock, timezone),
    estimatedEndTime: false, // default to false for now, no equivalent in the back end / attached logic in the front end
    duration: differenceInMinutes(eventBlock.item.endAt, eventBlock.item.startAt),
    venueLocation: findHierarchyEntityById(venues, (eventBlock.item as EventBlock).venueLocation.id) as Hierarchy<VenueLocation>,
    selectedEvents: eventBlock.children ? eventBlock.children : [],
    dataFieldValues: dataFieldIdToValueMapping(eventBlock.item.dataFieldValues.filter(({ dataField: { id } }) => id !== sessionCodeDataField.id)), // session code is handled separately
    zeroTime: eventBlock.item.zeroAt || ZERO_TIME_SPAN,
    zeroTimeDisabled: eventBlock.item.zeroAt == null,
});

export const StyledBody2 = styled(Body2)`
    color: ${(props) => props.theme.shades.two};
`;

export default CreateSessionComponent;
